import React, {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
} from 'react';
import { FaPaperPlane, FaStop } from 'react-icons/fa';
import { Button } from '@/ui';
import {
  Container,
  ContentWrapper,
  InputContainer,
  ContentEditableInput,
  Icon,
  SendButtonContainer,
  DropdownContainer,
  SearchContainer,
  SearchInput,
  CategoryContainer,
  CategoryButton,
  SuggestionsContainer,
  SuggestionItem,
  SuggestionItemContent,
  IconWrapper,
  DocumentInfo,
  DocumentTitle,
  DocumentMeta,
  PersonInfo,
  PersonAvatar,
  PersonName,
} from './styles';
import styled from 'styled-components';

const NoResultsContainer = styled.div`
  padding: 0.5rem;
  color: #6b7280;
`;

const AdminIcon = styled(Icon)`
  font-size: 0.75rem;
  color: #f59e0b;
`;

const SendIcon = styled(FaPaperPlane)`
  margin-right: 0.5rem;
`;

const StopIcon = styled(FaStop)`
  margin-right: 0.5rem;
`;

import { useEnhancedTextArea } from './hooks/useEnhancedTextArea';
import { useEnhancedDropdown } from './hooks/useEnhancedDropdown';
import { getFileIcon, segmentsToHtml } from './utils';
import { Command, Document, Person, EnhancedTextAreaProps } from './types';

export const EnhancedTextArea = forwardRef<
  {
    insertDocument: (doc: Document) => void;
    focus: () => void;
    getContent: () => string;
    setContent: (content: string) => void;
  },
  EnhancedTextAreaProps
>(
  (
    {
      placeholder = 'Type # to insert a document or command, @ to mention someone...',
      documents = [],
      commands = [],
      people = [],
      isLoading = false,
      onCancelGeneration,
      onSend,
      onSendMessage,
      onDocumentSelect: _onDocumentSelect,
      onCommandSelect: _onCommandSelect,
      onPersonSelect: _onPersonSelect,
      linkedDocumentIds = [],
      disableEnterToSend = false,
    },
    ref
  ) => {
    const {
      segments,
      showDropdown,
      searchTerm,
      activeCategory,
      activeCommand,
      selectedIndex,
      contentEditableRef,
      handleInput,
      handleKeyDown,
      handleDropdownKeyDown,
      handleContentClick,
      insertItem,
      insertDocument,
      setSearchTerm,
      setActiveCategory,
      isLoadingResponse,
      handleSendMessage: handleSendFromHook,
      dropdownType,
      setFilteredSuggestions,
    } = useEnhancedTextArea({
      filteredSuggestions: [],
      handleSend: async (segments) => {
        if (onSendMessage) {
          // Extract document IDs and mentioned user IDs from segments
          const documentIds: string[] = [];
          const mentionedUserIds: string[] = [];

          segments.forEach((segment) => {
            if (segment.type === 'document') {
              documentIds.push(String(segment.id));
            } else if (segment.type === 'person') {
              mentionedUserIds.push(segment.userId);
            }
          });

          await onSendMessage(segments, documentIds, mentionedUserIds);
        } else if (onSend) {
          await onSend(segments);
        }
      },
      disableEnterToSend,
    });

    const { filteredSuggestions, documentCategories } = useEnhancedDropdown({
      documents,
      commands,
      people,
      searchTerm,
      activeCategory,
      dropdownType,
      linkedDocumentIds,
    });

    const prevFilteredSuggestionsRef = React.useRef(filteredSuggestions);

    React.useEffect(() => {
      if (prevFilteredSuggestionsRef.current === filteredSuggestions) {
        return;
      }

      const prev = prevFilteredSuggestionsRef.current;
      const current = filteredSuggestions;

      if (
        prev.length === current.length &&
        prev.every(
          (item, index) =>
            item.id === current[index]?.id && item.type === current[index]?.type
        )
      ) {
        prevFilteredSuggestionsRef.current = filteredSuggestions;
        return;
      }

      prevFilteredSuggestionsRef.current = filteredSuggestions;
      if (setFilteredSuggestions) {
        setFilteredSuggestions(filteredSuggestions);
      }
    }, [filteredSuggestions]);

    const handleSendClick = (e: React.MouseEvent) => {
      e.preventDefault();
      void handleSendFromHook();
    };

    const handleCancelClick = (e: React.MouseEvent) => {
      e.preventDefault();
      onCancelGeneration?.();
    };

    const showStopButton =
      !!onCancelGeneration && (isLoading || isLoadingResponse);

    useImperativeHandle(
      ref,
      () => ({
        insertDocument: (doc: Document) => {
          insertDocument(doc);
        },
        focus: () => {
          if (contentEditableRef.current) {
            contentEditableRef.current.focus();
          }
        },
        getContent: () => {
          return contentEditableRef.current?.innerHTML || '';
        },
        setContent: (content: string) => {
          if (contentEditableRef.current) {
            contentEditableRef.current.innerHTML = content;
          }
        },
      }),
      [insertDocument]
    );

    const isInitialized = useRef(false);
    useEffect(() => {
      if (
        contentEditableRef.current &&
        !isInitialized.current &&
        segments.length > 0
      ) {
        const html = segmentsToHtml(segments);
        if (html) {
          contentEditableRef.current.innerHTML = html;
          isInitialized.current = true;
        }
      }
    }, [segments]);

    return (
      <Container>
        <ContentWrapper>
          <InputContainer>
            <ContentEditableInput
              ref={contentEditableRef}
              contentEditable={!isLoading && !isLoadingResponse}
              onInput={handleInput}
              onKeyDown={handleKeyDown}
              onClick={handleContentClick}
              data-placeholder={placeholder}
              data-tour="ask-session-chat-input"
              suppressContentEditableWarning
            />

            <SendButtonContainer>
              <Button
                variant={showStopButton ? 'outline' : 'outlineHover'}
                leftIcon={showStopButton ? <StopIcon /> : <SendIcon />}
                onClick={showStopButton ? handleCancelClick : handleSendClick}
                disabled={
                  showStopButton ? false : isLoading || isLoadingResponse
                }
                isLoading={
                  showStopButton ? false : isLoadingResponse || isLoading
                }
                data-tour="ask-session-send-button"
              >
                {showStopButton ? 'Stop' : 'Send'}
              </Button>
            </SendButtonContainer>

            {showDropdown && (
              <DropdownContainer
                $positionAbove={true}
                data-tour="ask-session-context-menu"
              >
                <SearchContainer>
                  <SearchInput
                    type="text"
                    placeholder={
                      dropdownType === 'person'
                        ? 'Search people...'
                        : activeCommand
                        ? 'Select a document...'
                        : 'Search documents...'
                    }
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleDropdownKeyDown}
                    autoFocus
                  />
                </SearchContainer>

                {Object.keys(documentCategories).length > 0 && (
                  <CategoryContainer>
                    {Object.entries(documentCategories).map(([key, label]) => (
                      <CategoryButton
                        key={key}
                        $active={activeCategory === key}
                        onClick={() => setActiveCategory(key)}
                      >
                        {label}
                      </CategoryButton>
                    ))}
                  </CategoryContainer>
                )}

                <SuggestionsContainer>
                  {filteredSuggestions.length === 0 ? (
                    <SuggestionItem $selected={false}>
                      <SuggestionItemContent $type="document">
                        <NoResultsContainer>
                          {dropdownType === 'document' && linkedDocumentIds.length === 0
                            ? 'Link documents via Active Documents to use them here.'
                            : 'No results found'}
                        </NoResultsContainer>
                      </SuggestionItemContent>
                    </SuggestionItem>
                  ) : (
                    filteredSuggestions.map((item, index) => (
                      <SuggestionItem
                        key={`${item.type}-${item.id}`}
                        $selected={selectedIndex === index}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          insertItem(item);
                          if (item.type === 'document' && _onDocumentSelect) {
                            _onDocumentSelect(item as Document);
                          } else if (
                            item.type === 'command' &&
                            _onCommandSelect
                          ) {
                            _onCommandSelect(item as Command);
                          } else if (
                            item.type === 'person' &&
                            _onPersonSelect
                          ) {
                            _onPersonSelect(item as Person);
                          }
                        }}
                      >
                        {item.type === 'command' ? (
                          <SuggestionItemContent $type="command">
                            <Icon className="fas fa-terminal" $marginRight />
                            <span>{item.name}</span>
                          </SuggestionItemContent>
                        ) : item.type === 'person' ? (
                          <SuggestionItemContent $type="person">
                            {(item as Person).image ? (
                              <PersonAvatar
                                src={(item as Person).image}
                                alt={(item as Person).name}
                              />
                            ) : (
                              <Icon className="fas fa-user" $marginRight />
                            )}
                            <PersonInfo>
                              <PersonName>{(item as Person).name}</PersonName>
                              {(item as Person).isOwner && (
                                <AdminIcon className="fas fa-crown" />
                              )}
                            </PersonInfo>
                          </SuggestionItemContent>
                        ) : (
                          <SuggestionItemContent $type="document">
                            <IconWrapper>
                              <Icon
                                className={`fas ${getFileIcon(
                                  (item as Document).type
                                )}`}
                                $large
                              />
                            </IconWrapper>
                            <DocumentInfo>
                              <DocumentTitle>{item.name}</DocumentTitle>
                              <DocumentMeta>
                                {(item as Document).type.toUpperCase()} •{' '}
                                {(item as Document).size}
                              </DocumentMeta>
                            </DocumentInfo>
                          </SuggestionItemContent>
                        )}
                      </SuggestionItem>
                    ))
                  )}
                </SuggestionsContainer>
              </DropdownContainer>
            )}
          </InputContainer>
        </ContentWrapper>
      </Container>
    );
  }
);
